import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { MixinLabelDatum, ProductLabelDatum, ProductsByCodeSlim } from '../input/products/types';
import { Dimension, Valence } from '../types';
import { isNil } from 'lodash';

/**
 * Creates the path to the top-most product only following primary links
 */
export function productLabelHierarchyChain(
  productsByCode: ProductsByCodeSlim,
  productCode: string,
  currentChain: string[] = [],
): string[] {
  const datum = productsByCode[productCode];
  if (!datum) {
    console.error('Unable to find datum for product code', productCode);
    return currentChain;
  }
  const floatingParent = datum.parents.find(p => !productsByCode[p.code].floating);
  if (!floatingParent) {
    return currentChain;
  }
  return productLabelHierarchyChain(productsByCode, floatingParent.code, currentChain.concat(floatingParent.code));
}

/**
 * Convert an impact code to a dimension
 */
export function toDimension(impactCode: string): Dimension {
  switch (impactCode.slice(0, 1)) {
    case 'E':
      return Dimension.ENVIRONMENT;
    case 'K':
      return Dimension.KNOWLEDGE;
    case 'S':
      return Dimension.SOCIAL;
    case 'H':
      return Dimension.HEALTH;
  }
  throw Error(`Unknown dimension for: ${impactCode}`);
}

export const HOTDOG_VERSION = 10;
export const EQUAL_WEIGHTS_TITLE = 'Equal weights';

// NOTE: This check might fail for product codes containing non-ascii characteres. This should be rare
//       but can still happen.
export function isValidProductCode(productCode?: string): productCode is string {
  return !!productCode && /^[A-Z0-9]{5}$/.test(productCode);
}

export function isValidMixinCode(productCode?: string): productCode is string {
  return !!productCode && /^[A-Z0-9]{8}$/.test(productCode);
}

export const mixinCodeLength = 8;
export const productCodeLength = 5;

export function isMixinCode(productCode: string) {
  return productCode.length === mixinCodeLength;
}

export function isMixin(
  product: Pick<ProductLabelDatum, 'mixinExpression' | 'mixin'> | null | undefined,
): product is MixinLabelDatum {
  if (!product) {
    return false;
  }
  return !isNil(product.mixinExpression) && product.mixin;
}

export function isImpactApplicable(impactCategoryCode: string, valence: Valence) {
  switch (impactCategoryCode) {
    case ImpactCategoryId.TAXES:
      return valence === Valence.POSITIVE;
    case ImpactCategoryId.JOBS:
      return valence === Valence.POSITIVE;
    case ImpactCategoryId.SOCIETAL_INFRA:
      return valence === Valence.POSITIVE;
    case ImpactCategoryId.KNOWLEDGE_INFRA:
      return valence === Valence.POSITIVE;
    case ImpactCategoryId.CREATES_KNOWLEDGE:
      return valence === Valence.POSITIVE;
    case ImpactCategoryId.SCARCE_HUMAN_CAPITAL:
      return valence === Valence.NEGATIVE;
  }
  return true;
}

function productCodeFromTitleWords(words: string[], targetLength: number) {
  let extractionLength = 2;
  let idCandidate;

  do {
    idCandidate = words
      .map(w => w.slice(0, extractionLength))
      .join('')
      .slice(0, targetLength)
      .toUpperCase();
  } while (idCandidate.length < targetLength && extractionLength++ < targetLength);

  if (idCandidate.length < targetLength) {
    idCandidate = padRight(idCandidate!, targetLength, 'A');
  }

  return idCandidate;
}

function padRight(str: string, length: number, char: string) {
  if (str.length < length) {
    const padString = char.repeat(length);
    return str.concat(padString.slice(0, length - str.length));
  }

  return str;
}

function increaseCharacterAtEnd(id: string): string {
  const charcodeForZ = 'Z'.charCodeAt(0);
  const lastCharcode = id.charCodeAt(id.length - 1);
  if (lastCharcode === charcodeForZ) {
    return `${increaseCharacterAtEnd(id.slice(0, -1))}A`;
  }
  return `${id.slice(0, -1)}${String.fromCharCode(lastCharcode + 1)}`;
}

/**
 * First try concatenating the first `range(2, 5-1)` letters from each word,
 * then replace the last char with an 'A' and start increasing the last characters.
 */
function* codeGenerator(words: string[], idLength: number) {
  let idCandidate = productCodeFromTitleWords(words, idLength);
  yield idCandidate;

  idCandidate = `${idCandidate.slice(0, idLength - 1)}A`;
  yield idCandidate;

  while (true) {
    idCandidate = increaseCharacterAtEnd(idCandidate);
    yield idCandidate;
  }
}

export function generateProductMixinCode(title: string, existingCodes: Set<string>) {
  return generateCode(title, existingCodes, mixinCodeLength);
}

export function generateCode(title: string, existingCodes: Set<string>, length: number): string {
  const skippedWords = ['and', 'to', 'for', 'a', 'other', 'or', 'of'];
  const punctuationToIgnore = /[.,\/#!$%\^&\*;:{}=_`'~()]/g;
  const words = title
    .replace(punctuationToIgnore, '')
    .replace('-', ' ') // Split dashed words into separate words
    .replace(/\s{2,}/g, ' ') // Remove multiple adjacent spaces
    .toLowerCase()
    .split(' ')
    .filter(word => skippedWords.indexOf(word) === -1);

  for (const idCandidate of codeGenerator(words, length)) {
    if (!existingCodes.has(idCandidate)) {
      return idCandidate;
    }
  }
  console.error('Unable to generate a product code! Should only get here if all charactor combinations exhausted.');
  return 'X'.repeat(length);
}
